"use client";
import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { authService } from "../services/auth";
import { apiOld } from "../services/api";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { CartProvider } from "./CartContext";
import { CartDrawer } from "./CartDrawer";
import LoginPage from "./LoginPage";
import { ReauthModal } from "./ReauthModal";

export default function Layout() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isReauthOpen, setIsReauthOpen] = useState(false);
  const lastActiveTimestampRef = useRef<number>(Date.now());
  const location = useLocation();

  const currentPage = location.pathname.split('/').filter(Boolean)[0] || '';

  const fetchProfile = async () => {
    try {
      const response = await authService.getProfile();
      setUserData(response.data.data || response.data);
      setIsAuthenticated(true);
      setIsReauthOpen(false);
    } catch (error: any) {
      console.error("Failed to fetch profile:", error);
      if (error.response?.status === 401) {
        if (isAuthenticated) {
          setIsReauthOpen(true);
        } else {
          setIsAuthenticated(false);
          setUserData(null);
          localStorage.removeItem('sanctum_token');
          localStorage.removeItem('accessToken');
        }
      }
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('sanctum_token');
      if (token) {
        await fetchProfile();
      }
      setIsCheckingAuth(false);
    };
    checkAuth();

    // Listener para eventos de sessão expirada vindos das requisições Axios (401)
    const handleSessionExpired = () => {
      setIsReauthOpen(true);
    };
    window.addEventListener('fdc:session_expired', handleSessionExpired);

    // Listener para mudança de visibilidade da aba (quando o admin volta para a tela após inatividade)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const minutesInactive = (now - lastActiveTimestampRef.current) / (1000 * 60);

        // Se esteve inativo por mais de 10 minutos e possui token, valida a sessão de forma silenciosa
        if (minutesInactive >= 10 && localStorage.getItem('sanctum_token')) {
          try {
            await authService.getProfile();
          } catch (error: any) {
            if (error.response?.status === 401) {
              setIsReauthOpen(true);
            }
          }
        }
        lastActiveTimestampRef.current = now;
      } else {
        lastActiveTimestampRef.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Ping o sistema antigo a cada 15 minutos para manter a sessão legada viva
    const keepAliveInterval = setInterval(() => {
      if (localStorage.getItem('sanctum_token')) {
        apiOld.get('/home').catch(() => { });
      }
    }, 15 * 60 * 1000); // 15 minutos

    return () => {
      window.removeEventListener('fdc:session_expired', handleSessionExpired);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(keepAliveInterval);
    };
  }, [isAuthenticated]);

  const handleLoginSuccess = async () => {
    setIsAuthenticated(true);
    setIsReauthOpen(false);
    await fetchProfile();
  };

  const handleExplicitLogout = () => {
    setIsReauthOpen(false);
    setIsAuthenticated(false);
    setUserData(null);
    localStorage.removeItem('sanctum_token');
    localStorage.removeItem('accessToken');
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <LoginPage onLogin={handleLoginSuccess} />
      </>
    );
  }

  return (
    <CartProvider>
      <div className="min-h-screen bg-gray-50 flex overflow-x-hidden">
        <Sidebar
          currentPage={currentPage}
          onPageChange={() => { }}
          user={userData}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          isMobileOpen={isMobileMenuOpen}
          onCloseMobile={() => setIsMobileMenuOpen(false)}
        />
        <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          <Header
            user={userData}
            onOpenMobileMenu={() => setIsMobileMenuOpen(true)}
          />
          <main className="flex-1">
            <Outlet context={{ user: userData, refetchUser: fetchProfile }} />
          </main>
        </div>
        <CartDrawer />
        <ReauthModal
          isOpen={isReauthOpen}
          userEmail={userData?.email}
          onSuccess={handleLoginSuccess}
          onLogout={handleExplicitLogout}
        />
      </div>
    </CartProvider>
  );
}
